import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";

// Feb 2026 · 27 Feb 2026 · Stabile Client-IDs für Line-Items, damit React
// beim Umsortieren/Löschen keine State-Korruption erzeugt (Review-Fix).
const _newLineId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `li-${Math.random().toString(36).slice(2)}-${Date.now()}`;
const _emptyLine = (label = "") => ({ _uid: _newLineId(), label, qty: 1, unit_price: 0, unit: "" });

import {
  X,
  Calendar as CalIcon,
  Clock,
  MapPin,
  User as UserIcon,
  DollarSign,
  Paperclip,
  ShieldCheck,
  ImageIcon,
  FileText as FileIcon,
  FileSpreadsheet,
  Plus,
  Trash2,
  Link2,
  Check,
  ExternalLink,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, STATUS_COLORS, withAlpha } from "../lib/bookingStatus";
import StatusHistoryTimeline from "./StatusHistoryTimeline";
import InlineSyncAssignChip from "./InlineSyncAssignChip";
import { deDate, deDateFullTime, deDateLong, deDateMediumShortYear } from "../lib/dateFormat";
import { deCurrency } from "../lib/numberFormat";
import { BookingLinkChip, MetaRow, RequestHeaderAssigneeLines } from "./RequestDetailParts";
import { useMarkSeen, SeenStatusLine } from "./SeenIndicator";

/**
 * Feb 2026 · User-Directive · Vollständige Detailansicht einer Anfrage.
 *
 * Öffnet sich per Klick auf Titel/Beschreibung einer Anfragen-Card in
 * `TeamRequests.jsx`. Zeigt alle vom Kunden eingegebenen Felder aus
 * `CustomerRequestNew.jsx`:
 *   • Titel + Beschreibung (voller Text, Umbrüche erhalten)
 *   • Kunde (Name) + Objekt (Property-Name)
 *   • Wunschdatum + Wunschzeit
 *   • Budget
 *   • Priorität + Freigabe-erforderlich-Flag
 *   • Anhänge (Bilder + Dokumente mit direktem Link zur Datei)
 * Zusätzlich zeigt es Team-relevante Metadaten (Source, Batch-ID,
 * Zugeordnete Buchung, Assigned-To, Status-Verlauf) — damit das Modal
 * sowohl Detail-Referenz als auch Audit-Trail ist.
 *
 * Reines Read-only-Fenster: Aktionen (Status ändern, Übernehmen, Zuweisung)
 * bleiben in der Card, damit der User zwischen Übersicht (Card) und
 * Detailtiefe (Modal) trennen kann. Das Modal ist per Escape und Click-
 * outside schließbar; scroll-Overflow im Body verhindert Body-Bleed.
 */

const REQ_STATUS_LABEL = {
  new: "Neu",
  received: "Eingegangen",
  under_review: "In Prüfung",
  quote_required: "Angebot benötigt",
  awaiting_approval: "Wartet auf Freigabe",
  scheduled: "Terminiert",
  in_progress: "In Bearbeitung",
  completed: "Erledigt",
  cancelled: "Storniert",
};

const PRIORITY_LABEL = {
  normal: "Normal",
  high: "Hoch",
  urgent: "Dringend",
};

const SOURCE_LABEL = {
  request_anything: "Sonderwünsche",
  extras_bulk: "Extraservice (aus Katalog)",
  team_captured: "Team-erfasste Aufgabe",
};

function backendUrl() {
  return process.env.REACT_APP_BACKEND_URL || "";
}
function fileUrl(id) {
  return `${backendUrl()}/api/files/${id}`;
}


export default function RequestDetailModal({ request, onClose, onLinkedChange, onChanged }) {
  const seenNow = useMarkSeen("request", request?.id, true);
  // Feb 2026 · User-Directive „Direkt-Zuweisen im Sync-Chip" (3 Sep 2026):
  // Wir spiegeln `request` in einen State, damit inline-Änderungen
  // (Concierge + Datum aus dem Sync-Chip) sofort im Modal sichtbar sind,
  // ohne dass die Elternkomponente ein Refresh triggern muss.
  const [r, setR] = useState(request);
  useEffect(() => { setR(request); }, [request]);
  const [quoteMode, setQuoteMode] = useState(null); // null | "create" | "link"
  const [quoteBusy, setQuoteBusy] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [chosenQuoteId, setChosenQuoteId] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newLines, setNewLines] = useState([_emptyLine()]);
  // Feb 2026 · User-Directive (3 Sep 2026) „Angebot aus Anfrage": wenn die
  // Anfrage KEIN Objekt hat, soll der Admin es beim Anlegen des Angebots
  // wählen können. Wir laden die Objekte des Kunden lazy, sobald der
  // „Neues Angebot erstellen"-Modus aktiv wird UND kein property_id gesetzt.
  const [pickedPropertyId, setPickedPropertyId] = useState("");
  const [customerProps, setCustomerProps] = useState([]);
  // Feb 2026 · Standard-Positionen für Angebote (P2). Der Extras-Katalog
  // liefert Vorlagen (Namen + optionaler Preis-Hinweis). Ein Klick fügt
  // eine neue Position mit vor-ausgefülltem Text + Preis an — Concierge
  // muss beim wiederkehrenden Kleingeschäft nichts mehr abtippen.
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Beim Wechsel in „Neues Angebot erstellen" die Titel/Beschreibung aus
  // der Anfrage vor-befüllen — Concierge muss nur noch Positionen ergänzen.
  // Läuft bewusst nur bei Modus-/ID-Wechsel (nicht bei jedem Poll), damit
  // begonnene Eingaben nicht überschrieben werden — aktuelles `request` via Ref.
  const requestRef = useRef(request);
  useEffect(() => { requestRef.current = request; });
  useEffect(() => {
    const req = requestRef.current;
    if (quoteMode === "create" && req) {
      setNewTitle(req.title || "");
      setNewDesc(req.description || "");
      setNewLines([_emptyLine(req.title || "Position")]);
      setPickedPropertyId(req.property_id || "");
      // Feb 2026 · User-Directive (3 Sep 2026): Objekte des Kunden nur
      // laden, wenn die Anfrage KEIN property_id trägt — sonst brauchen
      // wir den Picker nicht.
      if (!req.property_id && req.customer_id) {
        (async () => {
          try {
            const { data } = await api.get(`/properties?owner_id=${encodeURIComponent(req.customer_id)}`);
            setCustomerProps(Array.isArray(data) ? data : []);
          } catch (_e) { /* Silent — Picker bleibt leer, submitCreate zeigt später einen Toast. */ }
        })();
      }
    }
    if (quoteMode === "link" && req?.id) {
      (async () => {
        try {
          const { data } = await api.get(`/requests/${req.id}/quote-candidates`);
          setCandidates(data || []);
        } catch (e) { toast.error(formatErr(e.response?.data?.detail) || e.message); }
      })();
    }
  }, [quoteMode, request?.id]);

  // Feb 2026 · Vorlagen-Katalog einmal laden, sobald der Concierge in
  // „Neues Angebot erstellen" wechselt. `/api/extras` liefert Namen +
  // (optionalen) Preishinweis — Team-Editoren können den Katalog unter
  // `/team/settings/checklist-master` pflegen.
  useEffect(() => {
    if (quoteMode !== "create" || templates.length > 0) return;
    (async () => {
      try {
        const { data } = await api.get("/extras");
        setTemplates(Array.isArray(data) ? data : []);
      } catch (e) { /* Vorlagen sind optional — kein Toast bei Fehler. */ }
    })();
  }, [quoteMode, templates.length]);

  // Preis aus dem Text-Hint parsen ("ab 45 €/h" → 45). Fällt zurück auf 0
  // wenn keine Zahl gefunden — Concierge kann den Preis dann in-line anpassen.
  const parsePriceHint = (hint) => {
    if (!hint) return 0;
    const m = String(hint).replace(/\./g, "").replace(",", ".").match(/([0-9]+(?:\.[0-9]+)?)/);
    return m ? Number(m[1]) : 0;
  };

  const applyTemplate = (t) => {
    if (!t) return;
    const label = t.name_de || t.name_en || t.key || "";
    const price = parsePriceHint(t.price_hint);
    setNewLines((prev) => {
      // Wenn die erste Zeile noch leer ist, überschreibe sie — sonst append.
      const first = prev[0];
      const isFirstEmpty = !((first?.label || "").trim()) && (Number(first?.unit_price) || 0) === 0;
      const line = { label, qty: 1, unit_price: price, unit: "" };
      return isFirstEmpty ? [line, ...prev.slice(1)] : [...prev, line];
    });
    setTemplatesOpen(false);
    toast.success(`„${label}" als Position eingefügt.`);
  };

  if (!request) return null;
  const currency = "EUR";
  const total = newLines.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.unit_price) || 0), 0);

  const submitCreate = async () => {
    // Feb 2026 · User-Directive (3 Sep 2026): Objekt zunächst aus der
    // Anfrage nehmen; falls die Anfrage keins hat, greift der Objekt-
    // Picker (`pickedPropertyId`).
    const effectivePropId = r.property_id || pickedPropertyId;
    if (!effectivePropId) {
      toast.error("Bitte ein Objekt für das Angebot wählen.");
      return;
    }
    if (!newTitle.trim()) { toast.error("Titel ist erforderlich."); return; }
    const cleanLines = newLines
      .filter((l) => (l.label || "").trim())
      .map((l) => ({
        label: l.label.trim(),
        qty: Number(l.qty) || 1,
        unit_price: Number(l.unit_price) || 0,
        unit: l.unit || null,
      }));
    if (cleanLines.length === 0) { toast.error("Mindestens eine Position mit Bezeichnung."); return; }
    setQuoteBusy(true);
    try {
      await api.post("/quotes", {
        property_id: effectivePropId,
        title: newTitle.trim(),
        description: newDesc.trim() || null,
        line_items: cleanLines,
        currency,
        request_id: r.id,
      });
      toast.success("Angebot erstellt und mit der Anfrage verknüpft.");
      onLinkedChange?.();
      onClose?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setQuoteBusy(false); }
  };

  const submitLink = async () => {
    if (!chosenQuoteId) { toast.error("Bitte ein Angebot auswählen."); return; }
    setQuoteBusy(true);
    try {
      await api.patch(`/requests/${r.id}/link-quote`, { quote_id: chosenQuoteId });
      toast.success("Angebot verknüpft. Kunde sieht es zur Freigabe.");
      onLinkedChange?.();
      onClose?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setQuoteBusy(false); }
  };

  const unlinkQuote = async () => {
    if (!r.linked_quote_id) return;
    if (!window.confirm("Verknüpfung mit dem Angebot lösen?")) return;
    setQuoteBusy(true);
    try {
      await api.patch(`/requests/${r.id}/link-quote`, { quote_id: null });
      toast.success("Verknüpfung entfernt.");
      onLinkedChange?.();
      onClose?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setQuoteBusy(false); }
  };

  const addLine = () => setNewLines((prev) => [...prev, _emptyLine()]);
  const removeLine = (i) => setNewLines((prev) => prev.filter((_, idx) => idx !== i));
  const setLine = (i, k, v) => setNewLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  const isCancelled = r.status === "cancelled";
  const stripes = `repeating-linear-gradient(135deg, ${STATUS_COLORS.red} 0 4px, ${withAlpha(STATUS_COLORS.red, 0.35)} 4px 8px)`;
  const statusLabel = REQ_STATUS_LABEL[r.status] || r.status || "—";

  const dateStr = r.date_needed
    ? deDateLong(r.date_needed)
    : null;
  const timeStr = r.preferred_time || null;
  const createdStr = r.created_at
    ? deDateFullTime(r.created_at)
    : null;
  const budgetStr = r.budget ? String(r.budget).trim() : null;
  const attachments = Array.isArray(r.attachments) ? r.attachments : [];

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 md:p-8"
      style={{ background: withAlpha(BRAND_COLORS.navy, 0.55) }}
      onClick={onClose}
      data-testid="request-detail-modal"
    >
      <div
        className="bg-ivory w-full max-w-[820px] shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — Titel + Status-Pill */}
        <div className="relative border-b border-navy/10">
          {/* Systemweiter Status-Farbstreifen oben (4 px) */}
          <span
            aria-hidden="true"
            className="absolute left-0 right-0 top-0 h-1"
            style={{ background: isCancelled ? stripes : "transparent" }}
          />
          <div className="px-6 md:px-8 pt-8 pb-5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span
                    className="inline-flex items-center px-2 py-0.5 text-[10.5px] tracking-[0.14em] uppercase rounded-sm"
                    style={{
                      background: isCancelled ? stripes : (r.status === "completed" ? STATUS_COLORS.green : STATUS_COLORS.blue),
                      color: BRAND_COLORS.ivory,
                    }}
                    data-testid="request-detail-status-pill"
                  >
                    {statusLabel}
                  </span>
                  {r.priority && r.priority !== "normal" && (
                    <span
                      className="inline-flex items-center px-2 py-0.5 text-[10.5px] tracking-[0.14em] uppercase rounded-sm border"
                      style={{
                        borderColor: withAlpha("#D97706", 0.55),
                        color: "#B45309",
                        background: withAlpha("#D97706", 0.10),
                      }}
                    >
                      {PRIORITY_LABEL[r.priority] || r.priority}
                    </span>
                  )}
                  <span className="text-navy-dark/30">·</span>
                  <span className="label-caps text-navy-dark/60">{SOURCE_LABEL[r.source] || r.source || "Anfrage"}</span>
                </div>
                <h2 className="font-display text-2xl md:text-3xl text-navy leading-tight">
                  {r.title || "Anfrage"}
                </h2>
                {/* Feb 2026 · 11 Sep 2026 · Header-Zweizeiler (Haupt-
                    Concierge + Verantwortlich) — spiegelt Buchung + Projekt
                    für vollständige UX-Konsistenz über alle Detail-
                    Ansichten hinweg. */}
                <RequestHeaderAssigneeLines request={r} />
                <SeenStatusLine entity={{ ...(r || {}), ...(seenNow || {}) }} className="mt-2" testid="request-seen-status" />
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="request-detail-close"
                aria-label="Schließen"
                className="shrink-0 w-9 h-9 flex items-center justify-center text-navy-dark/60 hover:text-navy hover:bg-navy/5 rounded transition-colors"
              >
                <X className="w-5 h-5" strokeWidth={1.8} />
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 md:px-8 py-6 space-y-6 max-h-[calc(100vh-220px)] overflow-y-auto">
          {/* Feb 2026 · User-Directive „Bookings-Sync-Hinweis" (3 Sep 2026):
              Request-Anything wandert in die Bookings-Liste, sobald BEIDE
              Bedingungen — Zuweisung + Datum — erfüllt sind. Solange eines
              fehlt, zeigen wir hier einen goldenen Chip an, damit der Admin
              sofort weiß, was zum Sync fehlt. Kein Chip, wenn schon beides
              da ist oder Source ≠ request_anything. */}
          {r.source === "request_anything" && (!r.assigned_to || !r.date_needed) && (
            <InlineSyncAssignChip
              request={r}
              onSaved={(updated) => {
                setR(updated);
                onChanged?.(updated);
              }}
            />
          )}

          {/* Beschreibung — voller Text mit Zeilenumbrüchen */}
          {r.description && (
            <section data-testid="request-detail-description">
              <div className="label-caps mb-2">Beschreibung</div>
              <div className="bg-white border border-navy/10 p-4 text-[14px] text-navy-dark leading-relaxed whitespace-pre-wrap">
                {r.description}
              </div>
            </section>
          )}

          {/* Meta-Grid — Angaben des Kunden */}
          <section>
            <div className="label-caps mb-2">Angaben des Kunden</div>
            <div className="bg-white border border-navy/10 px-4 md:px-5 py-1">
              <MetaRow icon={UserIcon} label="Kunde" testid="request-detail-customer">
                {r.customer_name || "Nicht zugeordnet"}
              </MetaRow>
              <MetaRow icon={MapPin} label="Objekt" testid="request-detail-property">
                {r.property_name || <span className="italic text-navy-dark/50">Kein Objekt angegeben</span>}
              </MetaRow>
              <MetaRow icon={CalIcon} label="Wunschdatum" testid="request-detail-date">
                {dateStr || <span className="italic text-navy-dark/50">Kein Wunschdatum</span>}
              </MetaRow>
              {/* Feb 2026 · User-Directive · „Im Kalender anzeigen" —
                  Deep-Link auf den Kalender-Monat des Wunschdatums.
                  Nur klickbar, wenn ein Datum gesetzt ist. Öffnet in
                  einem neuen Tab, damit das Modal offen bleibt. */}
              {r.date_needed && (
                <div className="py-2 border-b border-navy/5" data-testid="request-detail-calendar-tile">
                  <a
                    href={`/team/calendar?date=${r.date_needed}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between gap-3 -mx-2 px-2 py-2 hover:bg-gold/[0.08] transition-colors"
                    data-testid="request-detail-open-in-calendar"
                    title="Kalender-Ansicht in neuem Tab öffnen"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <CalIcon className="w-4 h-4 shrink-0" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
                      <div className="min-w-0">
                        <div className="label-caps text-navy-dark/55 mb-0.5">Kalender</div>
                        <div className="text-[13.5px] text-navy leading-snug">
                          Im Kalender anzeigen
                          <span className="ml-2 text-[11.5px] text-navy-dark/50 italic">
                            → {dateStr}
                          </span>
                        </div>
                      </div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 shrink-0 text-navy-dark/40 group-hover:text-gold transition-colors" strokeWidth={1.8} />
                  </a>
                </div>
              )}
              {timeStr && (
                <MetaRow icon={Clock} label="Wunschzeit" testid="request-detail-time">
                  {timeStr} Uhr
                </MetaRow>
              )}
              {budgetStr && (
                <MetaRow icon={DollarSign} label="Budget" testid="request-detail-budget">
                  {budgetStr}
                </MetaRow>
              )}
              <MetaRow icon={ShieldCheck} label="Freigabe vor Kosten erforderlich" testid="request-detail-approval">
                {r.requires_approval === false ? "Nein — direkt beauftragen" : "Ja — Kunde freigibt jedes Angebot"}
              </MetaRow>
            </div>
          </section>

          {/* Anhänge */}
          {attachments.length > 0 && (
            <section data-testid="request-detail-attachments">
              <div className="label-caps mb-2">Anhänge · {attachments.length}</div>
              <div className="flex flex-wrap gap-2">
                {attachments.map((att) => {
                  const id = typeof att === "string" ? att : att?.id;
                  const name = typeof att === "object" ? (att?.name || att?.filename || id) : id;
                  const isImage = typeof att === "object" && att?.is_image;
                  if (!id) return null;
                  return (
                    <a
                      key={id}
                      href={fileUrl(id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`request-detail-attachment-${id}`}
                      className="inline-flex items-center gap-2 bg-white border border-navy/15 hover:border-gold px-3 py-2 text-[12.5px] text-navy transition-colors"
                    >
                      {isImage ? <ImageIcon className="w-4 h-4" strokeWidth={1.6} /> : <FileIcon className="w-4 h-4" strokeWidth={1.6} />}
                      <span className="truncate max-w-[240px]" title={name}>{name || "Datei"}</span>
                    </a>
                  );
                })}
              </div>
            </section>
          )}

          {/* Team-Info */}
          <section>
            <div className="label-caps mb-2">Team-Info</div>
            <div className="bg-white border border-navy/10 px-4 md:px-5 py-1">
              <MetaRow icon={CalIcon} label="Eingegangen">
                {createdStr}
              </MetaRow>
              {r.assignee && (
                <MetaRow icon={UserIcon} label="Bearbeitet von" testid="request-detail-assignee">
                  {r.assignee.name || r.assignee.email || "—"}
                  {r.assignee.role && (
                    <span className="text-navy-dark/50 label-caps ml-2">
                      · {r.assignee.role === "admin" ? "Admin" : "Concierge"}
                    </span>
                  )}
                </MetaRow>
              )}
              {r.batch_id && (
                <MetaRow icon={Paperclip} label="Sammel-Bestellung" testid="request-detail-batch">
                  Teil einer Sammel-Anfrage (Batch-ID: <span className="font-mono text-[12px]">{r.batch_id.slice(0, 8)}…</span>)
                </MetaRow>
              )}
              {r.booking_id && (
                <MetaRow icon={CalIcon} label="Verknüpfte Buchung" testid="request-detail-booking">
                  {/* Feb 2026 · User-Directive „Anfrage-Buchungs-Verlinkung im UI":
                      Klickbarer Chip mit Datum + Objekt-Name der verknüpften
                      Buchung. Ein Klick führt via ?open=<booking_id> direkt
                      in die /team/bookings-Liste, wo das BookingDetailModal
                      für diese Buchung auto-öffnet. */}
                  <BookingLinkChip booking={r.booking} bookingId={r.booking_id} />
                </MetaRow>
              )}
              {r.created_by_team && (
                <MetaRow icon={UserIcon} label="Erfasst durch">
                  Team (nicht vom Kunden)
                </MetaRow>
              )}
            </div>
          </section>

          {/* Feb 2026 · User-Directive „Angebot aus Anfrage" (3 Sep 2026):
              Section wird für JEDE Anfrage angezeigt — nicht mehr nur bei
              `requires_approval`. Header + Copy passen sich der jeweiligen
              Situation an, damit der Admin auch bei nicht freigabe-
              pflichtigen Anfragen ein Angebot direkt aus der Detailansicht
              anlegen kann. Backend verlinkt automatisch via `request_id`
              → `service_request.linked_quote_id`. */}
          <section data-testid="request-detail-quote-section">
              <div className="label-caps mb-2 flex items-center gap-2">
                <FileSpreadsheet className="w-3.5 h-3.5" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.6} />
                {r.requires_approval ? "Kostenfreigabe · Angebot" : "Angebot zur Anfrage"}
              </div>
              {r.linked_quote ? (
                <div
                  className="bg-white border-l-4 p-4"
                  style={{ borderLeftColor: r.linked_quote.status === "approved" ? STATUS_COLORS.green : r.linked_quote.status === "declined" ? STATUS_COLORS.red : STATUS_COLORS.blue }}
                  data-testid="request-detail-linked-quote"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="font-display text-lg text-navy leading-tight">{r.linked_quote.title}</div>
                      <div className="text-[12.5px] text-navy-dark/60 mt-1">
                        Gesamt: <span className="font-medium text-navy">
                          {deCurrency(r.linked_quote.total || 0, r.linked_quote.currency || "EUR")}
                        </span>
                        <span className="mx-2">·</span>
                        Status: <span className="label-caps" style={{ color: r.linked_quote.status === "approved" ? STATUS_COLORS.green : r.linked_quote.status === "declined" ? STATUS_COLORS.red : STATUS_COLORS.blue }}>
                          {r.linked_quote.status === "sent" ? "Gesendet" : r.linked_quote.status === "viewed" ? "Angesehen" : r.linked_quote.status === "approved" ? "Genehmigt" : r.linked_quote.status === "declined" ? "Abgelehnt" : r.linked_quote.status}
                        </span>
                        {r.linked_quote.decided_at && (
                          <span className="ml-2 text-navy-dark/50">
                            · {deDate(r.linked_quote.decided_at)}
                            {r.linked_quote.decided_by_name ? ` durch ${r.linked_quote.decided_by_name}` : ""}
                          </span>
                        )}
                      </div>
                      {r.linked_quote.customer_note && (
                        <div className="mt-2 italic text-navy-dark/70 text-[12.5px]">„{r.linked_quote.customer_note}"</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={unlinkQuote}
                      disabled={quoteBusy}
                      data-testid="request-detail-quote-unlink"
                      className="text-[10.5px] tracking-[0.14em] uppercase text-navy-dark/50 hover:text-red-attn underline decoration-dotted disabled:opacity-40"
                    >
                      Verknüpfung lösen
                    </button>
                  </div>
                </div>
              ) : quoteMode === null ? (
                <div className="bg-white border border-navy/10 p-4">
                  <p className="text-[13px] text-navy-dark leading-relaxed mb-3">
                    {r.requires_approval
                      ? "Der Kunde hat Kostenfreigabe verlangt. Erstellen oder verknüpfen Sie ein Angebot — der Kunde findet es dann in seiner Angebote-Ansicht zur Freigabe."
                      : "Für diese Anfrage kann direkt ein Angebot erstellt oder aus dem Bestand zugewiesen werden. Kunde erhält es zur Freigabe."}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setQuoteMode("create")}
                      data-testid="request-detail-quote-create-btn"
                      className="inline-flex items-center gap-1.5 text-ivory px-4 py-2 text-[11px] tracking-[0.15em] uppercase hover:opacity-90"
                      style={{ background: BRAND_COLORS.navy }}
                    >
                      <Plus className="w-3.5 h-3.5" strokeWidth={2} />
                      Angebot erstellen
                    </button>
                    <button
                      type="button"
                      onClick={() => setQuoteMode("link")}
                      data-testid="request-detail-quote-link-btn"
                      className="inline-flex items-center gap-1.5 border border-navy/25 hover:border-gold text-navy px-4 py-2 text-[11px] tracking-[0.15em] uppercase"
                    >
                      <Link2 className="w-3.5 h-3.5" strokeWidth={2} />
                      Bestehendes zuweisen
                    </button>
                  </div>
                </div>
              ) : quoteMode === "create" ? (
                <div className="bg-white border border-navy/10 p-4 space-y-3" data-testid="request-detail-quote-create-form">
                  {/* Feb 2026 · Objekt-Picker (User-Directive 3 Sep 2026):
                      Wenn die Anfrage kein Objekt trägt, muss der Admin
                      es hier setzen — sonst geht die Angebotsnummer-
                      Generierung im Backend nicht durch (property_id ist
                      Pflicht). Wenn `r.property_id` gesetzt ist, zeigen
                      wir stattdessen einen dezenten Read-only-Hinweis. */}
                  {r.property_id ? (
                    <div className="text-[11.5px] text-navy-dark/60 -mt-1 mb-1">
                      Objekt: <span className="text-navy font-medium">{r.property_name || "—"}</span>
                    </div>
                  ) : (
                    <div>
                      <label className="label-caps block mb-1">Objekt</label>
                      <select
                        value={pickedPropertyId}
                        onChange={(e) => setPickedPropertyId(e.target.value)}
                        data-testid="request-detail-quote-property-picker"
                        className="w-full border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px]"
                      >
                        <option value="">Objekt wählen …</option>
                        {customerProps.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                      {customerProps.length === 0 && (
                        <div className="text-[11.5px] text-navy-dark/50 mt-1 italic">
                          Der Kunde hat noch keine Objekte hinterlegt.
                        </div>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="label-caps block mb-1">Titel</label>
                      <input
                        value={newTitle}
                        onChange={(e) => setNewTitle(e.target.value)}
                        data-testid="request-detail-quote-title-input"
                        className="w-full border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px]"
                      />
                    </div>
                    <div>
                      <label className="label-caps block mb-1">Beschreibung</label>
                      <input
                        value={newDesc}
                        onChange={(e) => setNewDesc(e.target.value)}
                        className="w-full border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px]"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1 relative">
                      <label className="label-caps">Positionen</label>
                      {templates.length > 0 && (
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setTemplatesOpen((v) => !v)}
                            data-testid="request-detail-quote-templates-btn"
                            className="inline-flex items-center gap-1 text-[11px] tracking-[0.14em] uppercase text-navy-dark/70 hover:text-navy border border-navy/15 hover:border-gold px-2.5 py-1 bg-white"
                            title="Standard-Positionen aus dem Extras-Katalog einfügen"
                          >
                            <FileSpreadsheet className="w-3 h-3" strokeWidth={1.8} />
                            Vorlage einfügen
                          </button>
                          {templatesOpen && (
                            <>
                              <div
                                className="fixed inset-0 z-40"
                                onClick={() => setTemplatesOpen(false)}
                                aria-hidden="true"
                              />
                              <div
                                className="absolute right-0 mt-1 w-[320px] max-h-[280px] overflow-y-auto z-50 bg-white border border-navy/15 shadow-lg"
                                data-testid="request-detail-quote-templates-dropdown"
                              >
                                {templates.map((t) => {
                                  const label = t.name_de || t.name_en || t.key;
                                  const price = parsePriceHint(t.price_hint);
                                  return (
                                    <button
                                      key={t.id || t.key}
                                      type="button"
                                      onClick={() => applyTemplate(t)}
                                      data-testid={`request-detail-quote-template-${t.key || t.id}`}
                                      className="w-full text-left px-3 py-2 text-[12.5px] text-navy-dark border-b border-navy/5 last:border-b-0 hover:bg-navy/[0.03]"
                                    >
                                      <div className="font-medium leading-snug">{label}</div>
                                      {t.price_hint && (
                                        <div className="text-[11px] text-navy-dark/55 mt-0.5">
                                          Richtpreis: {t.price_hint}
                                          {price > 0 && ` · übernimmt ${deCurrency(price, "EUR")}`}
                                        </div>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="border border-navy/10">
                      <div className="grid grid-cols-12 gap-2 px-2 py-1.5 label-caps text-[9.5px] border-b border-navy/10 bg-navy/[0.02]">
                        <div className="col-span-6">Bezeichnung</div>
                        <div className="col-span-2 text-right">Menge</div>
                        <div className="col-span-3 text-right">Einzelpreis</div>
                        <div className="col-span-1"></div>
                      </div>
                      {newLines.map((li, i) => (
                        <div key={li._uid} className="grid grid-cols-12 gap-2 px-2 py-1.5 border-b border-navy/5 last:border-b-0 items-center">
                          <input value={li.label} onChange={(e) => setLine(i, "label", e.target.value)}
                            placeholder="z. B. Fensterputzer"
                            className="col-span-6 border border-navy/20 focus:border-gold focus:outline-none px-2 py-1 text-[12.5px]" />
                          <input type="number" step="0.1" value={li.qty} onChange={(e) => setLine(i, "qty", e.target.value)}
                            className="col-span-2 text-right border border-navy/20 focus:border-gold focus:outline-none px-2 py-1 text-[12.5px]" />
                          <input type="number" step="0.01" value={li.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)}
                            className="col-span-3 text-right border border-navy/20 focus:border-gold focus:outline-none px-2 py-1 text-[12.5px]" />
                          <button type="button" onClick={() => removeLine(i)}
                            disabled={newLines.length === 1}
                            className="col-span-1 text-navy-dark/40 hover:text-red-attn disabled:opacity-30 flex justify-center"
                            aria-label="Position entfernen">
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={1.6} />
                          </button>
                        </div>
                      ))}
                    </div>
                    <button type="button" onClick={addLine}
                      className="mt-2 inline-flex items-center gap-1 text-[11px] tracking-[0.14em] uppercase text-navy-dark/70 hover:text-navy">
                      <Plus className="w-3 h-3" strokeWidth={2} /> Position hinzufügen
                    </button>
                    <div className="mt-3 text-right text-[13px] text-navy-dark">
                      Gesamt: <span className="font-display text-lg text-navy ml-2">
                        {deCurrency(total, "EUR")}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <button type="button" onClick={submitCreate} disabled={quoteBusy}
                      data-testid="request-detail-quote-create-submit"
                      className="inline-flex items-center gap-1.5 text-ivory px-4 py-2 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
                      style={{ background: BRAND_COLORS.navy }}>
                      <Check className="w-3.5 h-3.5" strokeWidth={2.2} />
                      {quoteBusy ? "Speichert…" : "Angebot erstellen & senden"}
                    </button>
                    <button type="button" onClick={() => setQuoteMode(null)} disabled={quoteBusy}
                      className="text-[11px] tracking-[0.14em] uppercase text-navy-dark/60 hover:text-navy">
                      Abbrechen
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-navy/10 p-4 space-y-3" data-testid="request-detail-quote-link-form">
                  {candidates.length === 0 ? (
                    <p className="text-[13px] text-navy-dark/60 italic">Keine offenen Angebote für diesen Kunden vorhanden.</p>
                  ) : (
                    <>
                      <label className="label-caps block mb-1">Angebot auswählen</label>
                      <select
                        value={chosenQuoteId}
                        onChange={(e) => setChosenQuoteId(e.target.value)}
                        data-testid="request-detail-quote-link-select"
                        className="w-full border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px]"
                      >
                        <option value="">— Angebot auswählen —</option>
                        {candidates.map((q) => (
                          <option key={q.id} value={q.id}>
                            {q.title} · {deCurrency(q.total || 0, q.currency || "EUR")} · {q.status}
                          </option>
                        ))}
                      </select>
                    </>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <button type="button" onClick={submitLink} disabled={quoteBusy || !chosenQuoteId}
                      data-testid="request-detail-quote-link-submit"
                      className="inline-flex items-center gap-1.5 text-ivory px-4 py-2 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
                      style={{ background: BRAND_COLORS.navy }}>
                      <Link2 className="w-3.5 h-3.5" strokeWidth={2} />
                      Verknüpfen
                    </button>
                    <button type="button" onClick={() => setQuoteMode(null)} disabled={quoteBusy}
                      className="text-[11px] tracking-[0.14em] uppercase text-navy-dark/60 hover:text-navy">
                      Abbrechen
                    </button>
                  </div>
                </div>
              )}
            </section>

          {/* Status-Verlauf */}
          {Array.isArray(r.status_history) && r.status_history.length > 0 && (
            <section data-testid="request-detail-status-history">
              <div className="label-caps mb-2">Status-Verlauf</div>
              <div className="bg-white border border-navy/10 p-4">
                <StatusHistoryTimeline
                  history={r.status_history}
                  currentStatus={r.status}
                  testid={`request-detail-history-${r.id}`}
                />
              </div>
            </section>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
